// ---------------------------------------------------------------------------
// Pokémon TCG + Lorcana deck-list parsing — isomorphic (no DOM, no Bun APIs).
//
// Pokémon accepts the PTCG Live / Limitless export format:
//   Pokémon: 9
//   3 Charizard ex OBF 125
//   Trainer: 15
//   4 Ultra Ball SVI 196
//   Energy: 3
//   10 Basic {R} Energy SVE 2
//   Total Cards: 60
// The trailing "SET NUM" pair identifies the exact printing; energy lines
// with {R}-style symbols resolve by set/number, with the symbol expanded to
// a type name ("Fire Energy") as the name-search fallback.
//
// Lorcana accepts the Dreamborn / official-app export format:
//   4 Elsa - Spirit of Winter
//   2 Be Prepared
// where "Name - Version" is a character/location card and versionless lines
// are actions, songs and items.
// ---------------------------------------------------------------------------

/** One resolvable deck line. All entries land in the main deck. */
export interface TcgEntry {
  qty: number;
  name: string;
  /** Pokémon printing: PTCGO/Live set code (e.g. "OBF"), uppercased. */
  set?: string;
  /** Pokémon printing: collector number within the set (e.g. "125", "TG12"). */
  num?: string;
}

export interface ParsedTcgDeck {
  entries: TcgEntry[];
}

/** {R} → Fire etc. (PTCGL's basic-energy shorthand). */
const ENERGY_SYMBOLS: Record<string, string> = {
  G: "Grass", R: "Fire", W: "Water", L: "Lightning",
  P: "Psychic", F: "Fighting", D: "Darkness", M: "Metal", N: "Dragon",
};

/** Section/summary headers in PTCGL exports ("Pokémon: 9", "Total Cards: 60"). */
const POKE_HEADER = /^(pok[eé]mon|trainer|energy|total\s+cards)\s*:?\s*\d*$/i;

/**
 * A PTCGL card line: qty, name, then optionally "SET NUM".
 * Set codes are 2-6 chars, uppercase alnum with an optional dash (PR-SV);
 * numbers are alnum (125, 125a, TG12, GG44, SWSH250).
 */
const POKE_LINE = /^(\d+)\s*x?\s+(.+?)(?:\s+([A-Z][A-Z0-9-]{1,5})\s+([A-Za-z0-9]{1,8}))?\s*$/;

export function parsePokemonList(text: string): ParsedTcgDeck {
  const entries: TcgEntry[] = [];
  for (const raw of text.split("\n")) {
    const line = raw.trim();
    if (!line || POKE_HEADER.test(line)) continue;
    const m = POKE_LINE.exec(line);
    if (!m) continue;
    const qty = Math.min(99, Math.max(1, parseInt(m[1]!, 10)));
    // "Basic {R} Energy" → "Basic Fire Energy" (searchable name).
    const name = m[2]!
      .replace(/\{([A-Z])\}/g, (_, s: string) => ENERGY_SYMBOLS[s] ?? s)
      .trim();
    if (!name) continue;
    const entry: TcgEntry = { qty, name };
    if (m[3] && m[4]) {
      entry.set = m[3].toUpperCase();
      entry.num = m[4];
    }
    entries.push(entry);
  }
  return { entries };
}

/** A Lorcana line: "4 Elsa - Spirit of Winter" / "2x Be Prepared". */
const LORCANA_LINE = /^(\d+)\s*x?\s+(.+?)\s*$/;

export function parseLorcanaList(text: string): ParsedTcgDeck {
  const entries: TcgEntry[] = [];
  for (const raw of text.split("\n")) {
    const line = raw.trim();
    // Dreamborn exports occasionally carry a "Deck" header; skip bare words
    // without a leading count.
    if (!line) continue;
    const m = LORCANA_LINE.exec(line);
    if (!m) continue;
    const qty = Math.min(99, Math.max(1, parseInt(m[1]!, 10)));
    const name = m[2]!;
    if (!name) continue;
    entries.push({ qty, name });
  }
  return { entries };
}

/** Total card count of a parsed list (import-dialog preview + sanity caps). */
export function tcgDeckCount(deck: ParsedTcgDeck): number {
  return deck.entries.reduce((n, e) => n + e.qty, 0);
}
